import { useMemo, useState } from 'react';

import type { InstantSurveyType } from '@api/instant_survey/types';
import Page from '../../../../layouts/Page';
import ModalBuildSurvey from '../ModalBuildSurvey';
import { getSurveyTypesByCategory, mapSurveyTypeToRoute } from '../../utilities/helpers';
import DidYouKnow from './components/DidYouKnow';
import SurveyCard from './components/SurveyCard';

import styles from './_index.module.scss';

interface ResearchHubProps {
  surveyTypes: InstantSurveyType[];
}

const isModalTrigger = (surveyType: InstantSurveyType) =>
  surveyType.prompts.some((prompt) => prompt.isModalPrompt);

const ResearchHub = ({ surveyTypes }: ResearchHubProps) => {
  const [selectedSurveyType, setSelectedSurveyType] = useState<InstantSurveyType | null>(null);
  const standardSurvey = surveyTypes.find(
    (surveyType): surveyType is Extract<InstantSurveyType, { isSpecialty: false }> =>
      !surveyType.isSpecialty
  );
  const categories = useMemo(() => getSurveyTypesByCategory(surveyTypes), [surveyTypes]);

  return (
    <Page>
      <div className={styles['research-hub']}>
        <header className={styles['research-hub-header']}>
          <h1>Research Hub</h1>
          <p>Choose a research path designed for the question you want to answer.</p>
        </header>

        {standardSurvey && (
          <section
            aria-label="Primary research options"
            className={styles['research-hub-primary-area']}
          >
            <SurveyCard
              isModalTrigger={isModalTrigger(standardSurvey)}
              onSelect={setSelectedSurveyType}
              size="large"
              surveyType={standardSurvey}
            />
            <DidYouKnow />
          </section>
        )}

        <section className={styles['research-hub-methods']}>
          <header className={styles['research-hub-methods-header']}>
            <h2>Research methods</h2>
            <p>Start with a guided survey type matched to your research need.</p>
          </header>

          <div className={styles['research-hub-category-list']}>
            {categories.map(({ id, label, surveyTypes: categorySurveyTypes }) => (
              <section aria-labelledby={`research-category-${id}`} key={id}>
                <h3 id={`research-category-${id}`}>{label}</h3>
                <div className={styles['research-hub-survey-type-grid']}>
                  {categorySurveyTypes.map((surveyType) => (
                    <div key={surveyType.id}>
                      <SurveyCard
                        isModalTrigger={isModalTrigger(surveyType)}
                        onSelect={setSelectedSurveyType}
                        size="medium"
                        surveyType={surveyType}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      </div>

      {selectedSurveyType && (
        <ModalBuildSurvey
          isModalOpen
          route={mapSurveyTypeToRoute(selectedSurveyType)}
          setIsModalOpen={(isOpen) => {
            if (!isOpen) setSelectedSurveyType(null);
          }}
        />
      )}
    </Page>
  );
};

export default ResearchHub;
